import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, CircleX, RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";

import {
  clsxMerge,
  getCanonicalMeetingTime,
  safeGetDate,
} from "shared/lib/helpers";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import {
  CALENDAR_ACCEPTANCE_STATUS_COLORS_MAP,
  CALENDAR_ACCEPTANCE_STATUS_LABELS_MAP,
} from "shared/lib/constants/meeting-acceptance";
import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";
import { Avatar, AvatarFallback } from "shared/ui/shadcn/avatar";
import { DATE_FORMAT_WITHOUT_TZ } from "shared/lib/constants/time";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { useQueryFetchBookedMeetingCalendarEventForAccount } from "@/modules/pipeline/account-details/data/queries";
import {
  getFallbackTextForBookedMeetingEventAttendeeAvatar,
  getMeetingContactId,
} from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/utils";
import { useMutationRequestSyncCalendarEventForAccount } from "@/modules/pipeline/account-details/data/mutations";
import { AccountDetailsQueryKeys } from "@/modules/pipeline/account-details/data/keys";
import { useModifyMeeting } from "@/modules/pipeline/account-details/hooks/use-modify-meeting";
import {
  ACCOUNTS_DETAILS_TABS,
  ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME,
} from "@/modules/pipeline/account-details/constants";
import { BookedMeetingCalendarEventSectionStates } from "@/modules/pipeline/account-details/sections/booked-meeting-calendar-event-section/states";
import {
  BMCE_CANCEL_MEETING_MODAL_ID,
  MOVE_MISSING_CONTACT_MESSAGE,
  getMoveUnavailableMessage,
} from "@/modules/pipeline/account-details/sections/booked-meeting-calendar-event-section/constants";
import { JoinMeetingLink } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/join-meeting-link";
import {
  MOVE_MEETING_MODAL_ID,
  MoveMeetingModal,
} from "@/modals/move-meeting-modal";
import { AccountCancelMeetingConfirmationModal } from "@/modals/account-cancel-meeting-confirmation-modal";

export const BookedMeetingCalendarEventSection = ({
  className,
}: PropsWithClassNameI) => {
  const {
    accountStatus,
    account,
    campaign,
    activeTab,
    onAccountUpdate,
    accountHistoryData,
  } = useAccountDetailsContext();

  const queryClient = useQueryClient();
  const modifyMeeting = useModifyMeeting();

  const bookedMeetingCalendarEventApi =
    useQueryFetchBookedMeetingCalendarEventForAccount(
      campaign?.id,
      account?.id
    );

  const { mutateAsync, isPending } =
    useMutationRequestSyncCalendarEventForAccount();

  /**
   * When this card's Refresh last got an answer from the calendar.
   * "Last updated" is the event's own change time, so a check that finds no
   * change leaves it as it was (GLE-8461).
   */
  const [lastCheck, setLastCheck] = useState<{
    accountId?: string;
    checkedAt: string;
  } | null>(null);

  const checkedAt =
    lastCheck && lastCheck.accountId === account?.id
      ? lastCheck.checkedAt
      : null;

  // The status pill, the History tab and this card all read the meeting; a
  // change made here has to reach the first two as well.
  const reloadAccountAfterMeetingChange = useCallback(() => {
    onAccountUpdate?.();

    if (activeTab === ACCOUNTS_DETAILS_TABS.HISTORY) {
      accountHistoryData?.reloadData();
    }
  }, [onAccountUpdate, activeTab, accountHistoryData]);

  const invalidateCalendarEvent = useCallback(() => {
    if (!campaign?.id || !account?.id) {
      return;
    }

    void queryClient.invalidateQueries({
      queryKey: AccountDetailsQueryKeys.bookedMeetingCalendarEvent(
        campaign.id,
        account.id
      ),
    });
  }, [queryClient, campaign?.id, account?.id]);

  const handleSyncCalendarEvent = async () => {
    try {
      const event = await mutateAsync({
        accountId: account?.id,
        campaignId: campaign?.id,
      });

      setLastCheck({
        accountId: account?.id,
        checkedAt: new Date().toISOString(),
      });

      const isScheduled = Boolean(!bookedMeetingCalendarEventApi.data && event);

      const isRescheduled = Boolean(
        accountStatus?.meeting_time &&
        event &&
        accountStatus.meeting_time !== event.meeting_date
      );

      const isCanceled = Boolean(
        bookedMeetingCalendarEventApi.data && event === null
      );

      // Trigger account update if the meeting was scheduled, rescheduled or canceled
      if (isScheduled || isRescheduled || isCanceled) {
        reloadAccountAfterMeetingChange();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const meeting = bookedMeetingCalendarEventApi.data;

  // GLE-8249: a cancelled event keeps its row (stamped canceled_at) so the
  // card must read it as gone, the same as no event at all.
  const hasLiveEvent =
    !!meeting && meeting.event_exists && !meeting.canceled_at;

  const isMeetingScheduled =
    accountStatus?.status === ACCOUNT_DISPOSITION_STATUSES.MEETING_SCHEDULED;
  const canonicalMeetingTime = accountStatus
    ? getCanonicalMeetingTime(accountStatus)
    : null;
  const isMeetingUpcoming =
    !!canonicalMeetingTime && dayjs(canonicalMeetingTime).isAfter(dayjs());

  // Reschedule and Cancel act on the live event; a past meeting is handled by
  // the status menu's Modify button, and a cancelled one has nothing to act on.
  const canManageMeeting =
    hasLiveEvent && isMeetingScheduled && isMeetingUpcoming;

  const meetingContactId = getMeetingContactId(accountStatus);

  const handleReschedule = () => {
    if (meeting?.can_move_event && meetingContactId) {
      modalHelpers.open(MOVE_MEETING_MODAL_ID);

      return;
    }

    // The event cannot be rewritten on the AE's calendar, or the move cannot
    // be addressed to a lead, so the reschedule falls back to the booking
    // flow: a new slot, the old one cancelled.
    toast.error(
      meeting?.can_move_event
        ? MOVE_MISSING_CONTACT_MESSAGE
        : getMoveUnavailableMessage(meeting?.move_unavailable_reason)
    );
    modifyMeeting();
  };

  const handleCancel = () => {
    modalHelpers.open(BMCE_CANCEL_MEETING_MODAL_ID);
  };

  const handleCancelSuccess = () => {
    reloadAccountAfterMeetingChange();
    invalidateCalendarEvent();
  };

  return (
    <div className={clsxMerge("rounded-lg bg-white", className)}>
      <AccountDetailsSectionContainer
        title="Calendar event details"
        actions={
          <div className="flex items-center gap-3">
            {canManageMeeting && (
              <>
                <button
                  type="button"
                  aria-label="Reschedule meeting"
                  onClick={handleReschedule}
                  className={ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME}
                >
                  <CalendarClock size={14} aria-hidden="true" />
                  Reschedule
                </button>

                <button
                  type="button"
                  aria-label="Cancel meeting"
                  onClick={handleCancel}
                  className={ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME}
                >
                  <CircleX size={14} aria-hidden="true" />
                  Cancel
                </button>
              </>
            )}

            <button
              type="button"
              disabled={isPending}
              onClick={handleSyncCalendarEvent}
              className={clsxMerge(
                ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME,
                "disabled:opacity-50"
              )}
            >
              <RefreshCw
                size={14}
                aria-hidden="true"
                className={clsxMerge({ "animate-spin": isPending })}
              />
              {isPending ? "Syncing..." : "Refresh"}
            </button>
          </div>
        }
      >
        <DataStatesWrapper
          viewName="calendar event"
          api={bookedMeetingCalendarEventApi}
          loading={<BookedMeetingCalendarEventSectionStates.Pending />}
          error={
            <BookedMeetingCalendarEventSectionStates.Error
              onRetry={bookedMeetingCalendarEventApi.refetch}
            />
          }
        >
          <div className="flex flex-col gap-3">
            {meeting && hasLiveEvent ? (
              <>
                {meeting.meeting_date && (
                  <div className="typography-body-4">
                    <span className="typography-body-4-medium">
                      Scheduled:{" "}
                    </span>
                    <span className="text-black/70">
                      {safeGetDate(
                        meeting.meeting_date,
                        DATE_FORMAT_WITHOUT_TZ
                      )}
                    </span>
                  </div>
                )}

                <JoinMeetingLink conferenceUrl={meeting.conference_url} />

                <div className="flex flex-col gap-2">
                  <span className="typography-body-4-medium">Attendees:</span>
                  <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto">
                    {meeting.attendees.map((attendee, idx) => (
                      <div key={idx} className="flex gap-2">
                        <Avatar>
                          <AvatarFallback>
                            {getFallbackTextForBookedMeetingEventAttendeeAvatar(
                              attendee
                            )}
                          </AvatarFallback>
                        </Avatar>

                        <div className="flex max-w-[90%] flex-col gap-1 typography-body-4">
                          <div className="flex flex-col">
                            {attendee.name && (
                              <span className="truncate" title={attendee.name}>
                                {attendee.name}
                              </span>
                            )}

                            <span
                              className="truncate text-black/60"
                              title={attendee.email}
                            >
                              {attendee.email}
                            </span>
                          </div>

                          <div className="flex items-center gap-1 typography-body-5">
                            {attendee.is_organizer && (
                              <span className="text-muted-foreground">
                                Organizer &middot;
                              </span>
                            )}
                            <span
                              className={
                                CALENDAR_ACCEPTANCE_STATUS_COLORS_MAP[
                                  attendee.acceptance_status
                                ]
                              }
                            >
                              {
                                CALENDAR_ACCEPTANCE_STATUS_LABELS_MAP[
                                  attendee.acceptance_status
                                ]
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="text-muted-foreground typography-body-5">
                  Last updated:{" "}
                  {safeGetDate(meeting.updated_at, DATE_FORMAT_WITHOUT_TZ)}
                </p>
              </>
            ) : (
              <div className="italic text-black/30 typography-body-4">
                No calendar event found for this meeting
              </div>
            )}

            {checkedAt && (
              <p className="text-muted-foreground typography-body-5">
                Checked {safeGetDate(checkedAt, DATE_FORMAT_WITHOUT_TZ)}
              </p>
            )}
          </div>
        </DataStatesWrapper>
      </AccountDetailsSectionContainer>

      {canManageMeeting && (
        <>
          <MoveMeetingModal
            campaignId={campaign?.id}
            accountId={account?.id}
            contactId={meetingContactId}
            accountStatus={accountStatus}
            onMeetingChanged={reloadAccountAfterMeetingChange}
          />

          <AccountCancelMeetingConfirmationModal
            modalId={BMCE_CANCEL_MEETING_MODAL_ID}
            campaignId={campaign?.id}
            accountId={account?.id}
            onSuccess={handleCancelSuccess}
          />
        </>
      )}
    </div>
  );
};
